import { IInventoryPurchaseOrderBulkResponse } from "@/api_framework/api_modals/Inventory";
import AlertBlue from "@/app/components/ui/Alerts/Blue";
import AlertError from "@/app/components/ui/Alerts/Error";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { useInventoryPurchaseOrderBulkUploadAPI } from "@/app/hooks/api_hooks/Inventory/PO/useInventoryPurchaseOrderBulkUploadAPI";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { useLoader } from "@/app/hooks/useLoader";
import { useState } from "react";
import { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import toast from "react-hot-toast";
import { BiLoader } from "react-icons/bi";
import { FaFileAlt } from "react-icons/fa";
import { FiUpload } from "react-icons/fi";
import { IInventoryPurchaseOrderFormFields } from "../../auth_routes/InventoryManagement/OrderPurchaseComponent/OrderPurchaseAddComponent";
import ClipboardComponent from "@/app/components/ui/Clipboard";

const BulkUploadModalContent = ({ file, setFile, uploadError, setUploadError, uploadErrorData }) => {
  const [dragOver, setDragOver] = useState(false);
  const isBulkUploadInProgress = useLoader("isBulkUploadInProgress");
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    const allowedTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    if (selectedFile && allowedTypes.includes(selectedFile.type)) {
      setFile(selectedFile);
    } else {
      alert("Please select a CSV or Excel file.");
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const selectedFile = event.dataTransfer.files[0];
    const allowedTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    if (selectedFile && allowedTypes.includes(selectedFile.type)) {
      setFile(selectedFile);
    } else {
      alert("Please select a CSV or Excel file.");
    }
  };

  return (
    <div>
      {file ? (
        <div>
          <div className="flex justify-between items-center border-gray-400 mt-10 border-t-2 border-b-2 border-dotted w-full h-16">
            <div className="inline-flex items-center gap-2 mt-4">
              <FaFileAlt />
              <p className="text-gray-600">{file.name}</p>
            </div>
            <div className="flex items-center gap-1 text-[rgb(54,130,238)]">
              <FiUpload size={18} />
              <button
                className="mt-2 font-medium text-[rgb(54,130,238)] cursor-pointer"
                onClick={() => {
                  setFile(null);
                  setUploadError("");
                }}
              >
                Replace File
              </button>
            </div>
          </div>
          <div className="mt-6">
            {isBulkUploadInProgress && (
              <AlertBlue
                title={
                  <div className="flex justify-start items-center gap-x-3">
                    <p className="text-sm">Bulk Upload In Progress</p>
                    <span>
                      <BiLoader className="w-4 h-4 animate-spin" />
                    </span>
                  </div>
                }
              />
            )}
            {file && uploadError && uploadError?.length > 0 && (
              <div className="mt-4">
                <AlertError description={uploadError} title="Error While Uploading The File." />
                {uploadErrorData.length > 0 && (
                  <ul className="mt-5 text-sm text-black font-medium">
                    {uploadErrorData.map((error, index) => (
                      <li key={index} className="mt-2 flex items-center gap-2">
                        {index}.{""} {error}
                        <ClipboardComponent
                          className="ml-1"
                          tooltipText={`Copy ${error}`}
                          text={`${error}`}
                          copyId={`copy-user-code:${error}`}
                        />
                        {/* <button
                          onClick={() => navigator.clipboard.writeText(error)}
                          className="text-gray-600 hover:text-black"
                          title="Copy to clipboard"
                        >
                          <Clipboard size={16} />
                        </button> */}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div
          className={`p-2 overflow-auto bg-white rounded-[20px] flex-col justify-center items-center inline-flex border-2 border-dotted border-gray-400 h-64 w-full ${
            dragOver ? "drag-over" : ""
          }`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
        >
          <label className="flex items-center gap-2 font-medium text-[rgb(54,130,238)] cursor-pointer">
            <FiUpload className="mb-2" size={24} />
            Upload File
            <input type="file" accept=".csv, .xls, .xlsx" className="hidden" onChange={handleFileChange} />
          </label>
          <p className="mt-2 text-gray-600">Or drag & drop here</p>
        </div>
      )}
      <p className="mt-8 mb-8">
        Download a{" "}
        <a
          href="https://docs.google.com/spreadsheets/d/1ZkTUNRr3c-2rvCgNGoK1ArHGB9HuKHyVF2DEG3OWDu0/edit?gid=0#gid=0"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-[rgb(54,130,238)] underline underline-offset-2 cursor-pointer"
        >
          Sample CSV template
        </a>{" "}
        to see an example of the required format. You can download{" "}
        <a
          href="https://docs.google.com/spreadsheets/d/1cdB5vw6maJ46oqmevQyhq9ZhVyfnRd31O3favBa7Cak/edit?gid=0#gid=0"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-[rgb(54,130,238)] underline underline-offset-2 cursor-pointer"
        >
          Inventory masters CSV
        </a>{" "}
        to view all the SKUs in our system.
      </p>
    </div>
  );
};

interface IBulkUploadModalProps {
  formHook: UseFormReturn<IInventoryPurchaseOrderFormFields, any>;
  POProductsHook: UseFieldArrayReturn<IInventoryPurchaseOrderFormFields, "product_info", "id">;
  onUploadSuccess: (response: IInventoryPurchaseOrderBulkResponse) => void;
}

const BulkUploadModal: React.FC<IBulkUploadModalProps> = ({ formHook, onUploadSuccess }) => {
  const bulkUploadApi = useInventoryPurchaseOrderBulkUploadAPI();
  const [file, setFile] = useState(null);
  const { setInvisible } = useModalActions("BULK_UPLOAD_MODAL");
  const [uploadError, setUploadError] = useState<string>("");
  const [uploadErrorData, setUploadErrorData] = useState<any[]>([]); // Store data from 422 response

  return (
    <ModalPrompt
      modalId="BULK_UPLOAD_MODAL"
      showIcon={false}
      titleIcon=""
      dialogClassName="sm h-[500px] overflow-scroll"
      button={{
        primaryButtonText: "Upload & Continue",
        secondaryButtonText: "Cancel",
        primaryButtonClassname:
          "px-6 cursor-pointer py-[9px] bg-sky-900 hover rounded-md border border-slate-400 border-opacity-20 justify-center items-center gap-2 flex",
        primaryCallback: async () => {
          if (file) {
            setUploadError("");
            setUploadErrorData([]);
            try {
              const response = await bulkUploadApi({
                purchase_order_file: file,
                po_id: formHook.getValues("po_id"),
                branch_id: formHook.getValues("primary_purchase_info.vendor_details.branch_id"),
                bill_to_clinic_id: formHook.getValues("primary_purchase_info.clinic_details.bill_to_clinic_id"),
                ship_to_clinic_id: formHook.getValues("primary_purchase_info.clinic_details.ship_to_clinic_id"),
                bill_to_clinic_name: formHook.getValues("primary_purchase_info.clinic_details.bill_to_clinic_name"),
                ship_to_clinic_name: formHook.getValues("primary_purchase_info.clinic_details.ship_to_clinic_name"),
                branch_name: formHook.getValues("primary_purchase_info.vendor_details.branch_name"),
                vendor_id: formHook.getValues("primary_purchase_info.vendor_details.vendor_id"),
                vendor_name: formHook.getValues("primary_purchase_info.vendor_details.vendor_name"),
                delivery_date: formHook.getValues("primary_purchase_info.delivery_date"),
              });
              if (response?.status === false && response?.message) {
                setUploadError(response.message);
                setUploadErrorData(Array.isArray(response.data) ? response.data : [response.data]);
              } else {
                onUploadSuccess(response);
                setFile(null);
                setInvisible();
              }
            } catch (error) {
              toast.error("Failed to upload. Please try again.");
              console.error("Bulk upload error:", error);
            }
          } else {
            toast.error("Please select a file before uploading.");
          }
          // .then((res) => {
          //     if (res.status) {
          //       onUploadSuccess(res);
          //       setFile(null);
          //       setInvisible();
          //     }
          //     if (!res.status) {
          //       setUploadError(res.message);
          //     }
          //   });
          // } else {
          //   toast.error("Please select file");
          // }
        },
        secondaryCallback: () => {
          setInvisible();
        },
        shouldHideOnClick: false,
      }}
      title="Import SKUs via CSV or Excel"
      content={
        <BulkUploadModalContent
          setUploadError={setUploadError}
          uploadError={uploadError}
          uploadErrorData={uploadErrorData}
          setFile={setFile}
          file={file}
        />
      }
    />
  );
};

export default BulkUploadModal;
